/*
 * The groups section of the people directory and the administration's group table (spec 01 §11,
 * spec 12 §4.5), and the group picker other areas choose owners with. Four labels this area also
 * renders — the section's title, its empty state, the "Mentionable" column and the member count —
 * belong to the directory's catalogue (`core/people/i18n/en.ts`), because the section is part of
 * that directory.
 */

export const en = {
	"groups.description":
		"A group is a named set of people. Writing its handle in a message reaches everyone in it.",

	// Finding one. A group is found by its own two names or by the name of anybody in it, which is how
	// a reader who knows the people and not the handle looks one up.
	"groups.filter.label": "Find a group",
	"groups.filter.placeholder": "Group, handle or member",
	"groups.count": "{count} groups",
	"groups.count.one": "{count} group",
	"groups.countFiltered": "{shown} of {count} groups",
	"groups.countFiltered.one": "{shown} of {count} group",
	"groups.noMatch": "No group matches that.",

	// Making one.
	"groups.create": "New group",
	"groups.create.submit": "Create group",
	"groups.created": "{name} was created.",

	// The fields a group has.
	"groups.field.name": "Name",
	"groups.field.handle": "Handle",
	"groups.field.handle.hint":
		"Lower-case letters, digits, dots, dashes and underscores. This is what a mention writes.",
	"groups.field.mentionable": "Can be mentioned",
	"groups.field.mentionable.hint": "A message that writes the handle notifies every member.",
	// A group that can be mentioned wears the "Mentionable" pill on its card, and one that cannot
	// says so on its own page: the "Yes" and "No" of the table that used to list them are gone with
	// the table (X-A3 P7).
	"groups.notMentionable": "Cannot be mentioned",

	// One group's page.
	"groups.back": "All groups",
	"groups.notFound": "This group does not exist.",
	"groups.notMember": "You are not in this group, so you cannot change it.",
	// An ordinary group may not take a special's name: two "Team members" in a picker would be told
	// apart only by their handles.
	"groups.form.nameReserved": "That name belongs to a built-in group.",
	"groups.save": "Save changes",
	"groups.saved": "Saved.",

	// Members.
	"groups.members.title": "Members",
	"groups.members.add": "Add people",
	"groups.members.addSubmit": "Add to group",
	"groups.members.added": "{count} people added.",
	"groups.members.added.one": "{count} person added.",
	// The word on the button, and the name a screen reader reads out: a list of five members would
	// otherwise draw "Remove Anna Ruiz" five times over.
	"groups.members.remove": "Remove",
	"groups.members.removeNamed": "Remove {name}",
	"groups.members.removeTitle": "Remove {name}?",
	"groups.members.removeBody":
		"They stop being part of the group, and a mention of it no longer reaches them.",
	// An administrator who ticks themself in a group they are not in is told what joining hands them
	// before the password prompt asks for it.
	"groups.members.addSelfAsAdmin.title": "Add yourself to {name}?",
	"groups.members.addSelfAsAdmin.body":
		"You will also see the channels and AI chats this group owns, and its members see you in it. As an administrator you confirm this with your password.",

	// The rename and the handle folded away under a row of their own: a member opens a group's page to
	// see who is in it, and the two fields that change it are not what the page is for (D-10 P12).
	"groups.settings.reveal": "Rename this group",

	// Leaving and deleting.
	"groups.leave": "Leave group",
	"groups.leaveTitle": "Leave {name}?",
	"groups.leaveBody": "You stop being part of the group. Anyone in it can add you again.",
	"groups.delete": "Delete group",
	"groups.deleteTitle": "Delete {name}?",
	"groups.deleteBody":
		"The group disappears for everyone. Messages that already mention it stay as they are. Channels it owns become visible to their members only, and AI chats that only this group owns go to Team members.",
	"groups.deleted": "{name} was deleted.",

	// W-05 — the group card (X-A3 P7): one line under the name, the way a person's card says the
	// handle. The handle comes without its "@" from the model, and the text writes it.
	"groups.card.meta": "@{handle} · {count} members",
	"groups.card.meta.one": "@{handle} · {count} member",
	// A special group says on the same line who decides its members (`groups.special.<kind>.rule`):
	// beside the name, a pill would cut the name short once the card also shows five faces.
	"groups.card.metaSpecial": "@{handle} · {count} members · {rule}",
	"groups.card.metaSpecial.one": "@{handle} · {count} member · {rule}",

	// The three special groups (GUIDELINES.md § Groups). Each is called by its `.name` until
	// somebody renames it; its card says in `.rule` who decides its members, and its page says it
	// in full in `.explain`.
	"groups.special.all.name": "Everyone",
	"groups.special.all.rule": "automatic",
	"groups.special.all.explain":
		"Everyone with an account is in this group, guests included. Nobody adds or removes anyone by hand.",
	"groups.special.team.name": "Team members",
	"groups.special.team.rule": "set by administrators",
	"groups.special.team.explain":
		"Everyone who is not a guest. Only an administrator adds or removes somebody, and every administrator is in it.",
	"groups.special.admins.name": "Administrators",
	"groups.special.admins.rule": "set by administrators",
	"groups.special.admins.explain":
		"Administrators manage accounts, groups and channels. Only an administrator adds or removes one, and the last one stays.",
	// Said to an administrator on the two pages whose rows are standing: a change there ends the
	// person's sessions, so the page warns before anybody presses anything.
	"groups.special.signsOut":
		"Everybody whose access changes here is signed out on every device and signs in again.",
	// A guest reads the groups and changes none, not even one they are in.
	"groups.guestReadOnly": "As a guest you can see groups, but not change them.",

	// The group picker (GroupPicker.tsx): two prominent answers and a rarely used way to choose any
	// set. The copy names no module, because what the chosen groups get — owning an AI chat, say — is
	// the caller's to say in its own label.
	"groups.choose.team.hint": "Everyone on the team · {count} people",
	"groups.choose.team.hint.one": "Everyone on the team · {count} person",
	"groups.choose.me": "Only me",
	"groups.choose.me.hint": "Nobody else",
	"groups.choose.chosen": "Chosen: {names}",
	"groups.choose.change": "Change",
	"groups.choose.title": "Choose groups",
	"groups.choose.lead": "Everyone in at least one of the chosen groups is included.",
	"groups.choose.filter.label": "Find a group or person",
	"groups.choose.filter.placeholder": "Name or handle",
	"groups.choose.groups": "Groups",
	"groups.choose.people": "People",
	"groups.choose.noMatch": "Nothing matches that.",
	"groups.choose.atLeastOne": "Choose at least one.",
	"groups.choose.notIn":
		"You are not in any of the chosen groups, so you would not have access yourself.",
	"groups.choose.apply": "Use these",

	// The people picker. It serves the groups screens, "Add people" and "New message" alike, so the
	// copy names no group: the same sentence appears under a channel dialog.
	"groups.picker.label": "Find a person",
	"groups.picker.placeholder": "Name or handle",
	"groups.picker.empty": "Everybody is here already.",
	"groups.picker.selected": "{count} selected",
} as const satisfies Record<string, string>
